import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import useSeo from '@/hooks/useSeo';

const faqGroups = [
  {
    title: '阅读模式与画质',
    items: [
      {
        q: '高清原图模式如何保证画质？',
        a: '漫蛙漫画网为连载作品保留最高 4K 的无损原图资源。开启高清原图模式后，系统会根据当前屏幕分辨率智能匹配清晰度档位，即使全屏放大，线条、网点与灰阶层次依然清晰锐利。',
      },
      {
        q: '夜间模式与手机系统深色模式会冲突吗？',
        a: '不会。夜间模式提供独立的亮度与色温调节，可以跟随系统自动切换，也可以手动锁定。开启后界面自动转为柔和的深色，降低深夜追更时的眩光。',
      },
      {
        q: '双页浏览适合哪些设备使用？',
        a: '双页浏览主要面向横屏的平板和桌面设备，模拟实体单行本的翻页体验。在竖屏手机上会自动切换为单页滚动阅读，避免字号过小难以辨认。',
      },
      {
        q: '缩放设置支持哪些操作方式？',
        a: '支持多点触控缩放与滑杆两种方式，可以精确框选想要放大的分镜、对话框和细节画面，聚焦作者精心刻画的每一处笔触。',
      },
    ],
  },
  {
    title: '账号与设备',
    items: [
      {
        q: '可以跨设备同步阅读进度吗？',
        a: '可以。登录账号后，阅读进度、书签与收藏会自动同步到手机、平板和电脑，切换设备也能无缝接续上次阅读的位置。',
      },
      {
        q: '在电脑上支持滚动阅读吗？',
        a: '支持。滚动阅读不仅适合手机竖屏，也适用于桌面浏览器，配合鼠标滚轮即可一划到底，体验流畅。',
      },
      {
        q: '如何开启跟随系统的自动夜间切换？',
        a: '在阅读器设置中找到“外观”选项，开启“跟随系统”即可。系统切换深色模式时，阅读器会自动同步切换到夜间模式。',
      },
    ],
  },
  {
    title: '内容与更新',
    items: [
      {
        q: '漫画多久更新一次？',
        a: '连载作品会按作者更新节奏及时同步，热门作品通常每日更新。更新后会在书架和漫画库中优先展示，方便你第一时间追更。',
      },
      {
        q: '同一部作品可以保存多个书签吗？',
        a: '可以。你可以在任意章节添加多个书签并标注备注，方便跳回某个重要分镜或剧情节点。',
      },
    ],
  },
];

export default function Faq() {
  useSeo({
    title: '漫蛙漫画网常见问题 - 阅读模式、画质、账号同步与更新',
    description: '漫蛙漫画网常见问题解答：高清原图与画质、夜间模式、双页浏览、缩放设置、滚动阅读、账号同步与内容更新等阅读疑问一网打尽。',
    canonicalPath: '/faq',
  });

  const flatFaqs = faqGroups.flatMap((g) => g.items);

  useEffect(() => {
    const existing = document.getElementById('faq-schema');
    if (existing) existing.remove();
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-schema';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: flatFaqs.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('faq-schema')?.remove();
    };
  }, []);

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 pb-10 pt-24 md:px-6 md:pt-32">
          <nav aria-label="面包屑" className="text-xs text-foreground-500">
            <ol className="flex items-center gap-1.5">
              <li><a href="/" className="hover:text-primary-600">首页</a></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-700">常见问题</li>
            </ol>
          </nav>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">常见问题</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            关于阅读模式、画质、账号同步与内容更新的常见疑问，都可以在这里找到答案。没有找到？欢迎通过联系我们获取帮助。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-12 md:px-6 md:py-16">
        <div className="space-y-12">
          {faqGroups.map((group) => (
            <div key={group.title}>
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-primary-500"></span>
                {group.title}
              </h2>
              <div className="mt-5 space-y-4">
                {group.items.map((item) => (
                  <details
                    key={item.q}
                    className="group rounded-lg border border-background-200 bg-background-100 open:bg-background-50"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-sm font-semibold text-foreground-950 md:text-base">
                      {item.q}
                      <i className="ri-add-line text-xl text-primary-600 transition-transform group-open:rotate-45"></i>
                    </summary>
                    <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-lg border border-primary-200 bg-primary-500/5 p-8 text-center">
          <i className="ri-question-line text-3xl text-primary-600"></i>
          <h2 className="mt-3 font-heading text-lg font-bold text-foreground-950">还有疑问？</h2>
          <p className="mt-2 text-sm text-foreground-600">
            欢迎到关于我们页面查看联系方式，我们会尽快为你解答。
          </p>
          <Link
            to="/about"
            className="mt-5 inline-flex whitespace-nowrap items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
          >
            联系漫蛙 <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>
    </div>
  );
}